import { type LessonItem, addDays, canJoinLesson, timeOf, todayIso } from '@shkola/api';
import type { ToneName } from '@shkola/design';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CalendarDotsIcon, CheckCircleIcon, type FilledIcon } from '../../design/icons';
import { useMinute } from '../../lib/now';
import {
  type StudentContext,
  capitalize,
  dayMonthLong,
  dayNow,
  minutesBetween,
  roomOf,
  useShownDay,
  useTodo,
  weekdayKey,
} from './model';

/*
 * What matters now, for the greeting of every look (UX of 26.10, docs/ux-home-student.md §5–6).
 */

/** «, каб. 203» — the place after a subject, when there is one. */
export const placeOf = (t: TFunction, l: LessonItem) => {
  const room = l.format === 'online' ? t('studentHome.head.online') : roomOf(t, l.room);
  // «Комп’ютерний клас» inside a sentence: «, комп’ютерний клас»
  return room ? `, ${room.charAt(0).toLowerCase()}${room.slice(1)}` : '';
};

export interface Chip {
  tone: ToneName;
  icon?: FilledIcon;
  live?: boolean;
  text: string;
}

/**
 * What matters now: where the day stands (a chip), one sentence, a quiet line of details and the one
 * action if there is one — before the lessons the first one, during them the lesson or the break going
 * on and what is next, after them and on days off the next school day, what is due on it and what is
 * overdue.
 */
export function useHeadline(ctx: StudentContext) {
  const { t } = useTranslation();
  const now = useMinute();
  const day = useShownDay(ctx);
  const todo = useTodo(ctx);
  const today = todayIso();
  const status = dayNow(day.todays, now);
  let chip: Chip;
  let main = '';
  const details: string[] = [];
  let join: LessonItem | undefined;
  const minutes = (iso: string) => Math.max(1, Math.ceil((Date.parse(iso) - now) / 60_000));

  if (status.held.length > 0 && !status.over) {
    if (status.before && status.first) {
      const wait = minutesBetween(now, Date.parse(status.first.startsAt));
      chip =
        wait <= 60
          ? { tone: 'teal', live: true, text: t('studentHome.head.chip.before', { min: wait }) }
          : {
              tone: 'blue',
              icon: CalendarDotsIcon,
              text: t('studentHome.head.chip.today', {
                lessons: t('student.lessonsCount', { count: status.held.length }),
                from: timeOf(status.first.startsAt),
                to: timeOf((status.last as LessonItem).endsAt),
              }),
            };
      main = t('studentHome.head.first', {
        time: timeOf(status.first.startsAt),
        subject: status.first.subject,
        place: placeOf(t, status.first),
      });
      const tests = todo.testsOn(today);
      if (tests.length)
        details.push(t('studentHome.head.testToday', { subjects: tests.join(', ') }));
      if (canJoinLesson(status.first, today) && wait <= 15) join = status.first;
    } else if (status.live) {
      chip = {
        tone: 'green',
        live: true,
        text: t('studentHome.head.chip.live', { min: minutes(status.live.endsAt) }),
      };
      main = t('studentHome.head.live', {
        subject: status.live.subject,
        place: placeOf(t, status.live),
      });
      details.push(
        status.next
          ? t('studentHome.head.then', {
              subject: status.next.subject,
              time: timeOf(status.next.startsAt),
              place: placeOf(t, status.next),
            })
          : t('studentHome.head.last'),
      );
      if (canJoinLesson(status.live, today)) join = status.live;
    } else {
      const next = status.next as LessonItem;
      chip = {
        tone: 'teal',
        live: true,
        text: t('studentHome.head.chip.break', { min: minutes(next.startsAt) }),
      };
      main = t('studentHome.head.break', {
        subject: next.subject,
        time: timeOf(next.startsAt),
        place: placeOf(t, next),
      });
      const left = status.held.filter((l) => Date.parse(l.startsAt) >= Date.parse(next.startsAt));
      details.push(
        t('studentHome.head.left', {
          lessons: t('student.lessonsCount', { count: left.length }),
          time: timeOf((status.last as LessonItem).endsAt),
        }),
      );
      if (canJoinLesson(next, today)) join = next;
    }
  } else {
    // the next school day: «на завтра» / «на понеділок», «Завтра контрольна» / «У понеділок контрольна»
    const next = day.isToday ? undefined : day.date;
    const tomorrow = next === addDays(today, 1);
    const first = day.lessons.find((l) => l.status !== 'cancelled');
    chip = status.over
      ? {
          tone: 'green',
          icon: CheckCircleIcon,
          text: t('studentHome.head.chip.over', {
            time: timeOf((status.last as LessonItem).endsAt),
          }),
        }
      : { tone: 'blue', icon: CalendarDotsIcon, text: t('studentHome.head.chip.free') };
    const onNext = next
      ? tomorrow
        ? t('studentHome.head.tomorrow')
        : t(`student.on.${weekdayKey(next)}`).replace(/^у /, '')
      : '';
    const due = next
      ? todo.groups.flatMap((g) => g.items).filter((i) => i.due === next && i.kind !== 'test')
          .length
      : 0;
    if (status.over && next)
      main =
        due > 0
          ? t('studentHome.head.dueOnMain', {
              day: onNext,
              tasks: t('studentHome.head.tasks', { count: due }),
            })
          : t('studentHome.head.nothingDueMain', { day: onNext });
    else if (next && first) {
      const far = next > addDays(today, 6) ? `, ${dayMonthLong(next)}` : '';
      main = t('studentHome.head.free', {
        day: `${t(`student.on.${weekdayKey(next)}`)}${far}`,
        time: timeOf(first.startsAt),
      });
    } else main = t('studentHome.head.none');
    if (next) {
      const tests = todo.testsOn(next);
      if (tests.length)
        details.push(
          t('studentHome.head.testOn', {
            day: tomorrow
              ? t('studentHome.head.tomorrowCap')
              : capitalize(t(`student.on.${weekdayKey(next)}`)),
            subjects: tests.join(', '),
          }),
        );
      if (!status.over)
        details.push(
          due > 0
            ? t('studentHome.head.dueOn', {
                day: onNext,
                tasks: t('studentHome.head.tasks', { count: due }),
              })
            : t('studentHome.head.nothingDue', { day: onNext }),
        );
    }
    const overdue = todo.count('overdue');
    if (overdue > 0)
      details.push(
        t('studentHome.head.overdue', { tasks: t('studentHome.head.tasks', { count: overdue }) }),
      );
  }
  return { chip, main, details, join, ready: !day.pending && !todo.pending };
}
